import { useState, useEffect } from "react";
import { api, apiError } from "@/api";

const CATEGORIES = [
  "DJ",
  "Music artist or live performer",
  "Visual artist",
  "Photographer",
  "Videographer",
  "Dancer or movement artist",
  "Vendor",
  "Volunteer or production support",
  "Creative collaborator",
  "Other",
];

// key, label, type (text|textarea|url|select), options
const CATEGORY_FIELDS = {
  DJ: [
    ["dj_name", "DJ NAME", "text"],
    ["mix_link", "MIX LINK (SOUNDCLOUD / MIXCLOUD)", "url"],
    ["genres", "PRIMARY GENRES & REGIONAL SOUNDS", "textarea"],
    ["three_artists", "THREE ARTISTS OR TRACKS THAT REPRESENT YOUR SOUND", "textarea"],
    ["experience", "PREVIOUS PERFORMANCE EXPERIENCE", "textarea"],
    ["tech", "TECHNICAL REQUIREMENTS", "textarea"],
    ["availability", "AVAILABILITY", "text"],
    ["press_photo", "PRESS PHOTO LINK (DRIVE / DROPBOX / WEBSITE)", "url"],
  ],
  "Music artist or live performer": [
    ["discipline", "DISCIPLINE", "text"],
    ["reel", "PORTFOLIO OR PERFORMANCE REEL LINK", "url"],
    ["tech", "INSTALLATION OR TECHNICAL REQUIREMENTS", "textarea"],
    ["length", "TYPICAL PERFORMANCE LENGTH", "text"],
    ["availability", "AVAILABILITY", "text"],
  ],
  Photographer: [
    ["portfolio", "PORTFOLIO LINK", "url"],
    ["nightlife", "EVENT / NIGHTLIFE EXPERIENCE", "textarea"],
    ["equipment", "EQUIPMENT", "text"],
    ["medium", "PHOTO, VIDEO OR BOTH", "select", ["Photo", "Video", "Both"]],
    ["turnaround", "TURNAROUND TIME", "text"],
    ["rates", "RATES OR COLLABORATION EXPECTATIONS", "textarea"],
  ],
  Videographer: [
    ["portfolio", "PORTFOLIO LINK", "url"],
    ["nightlife", "EVENT / NIGHTLIFE EXPERIENCE", "textarea"],
    ["equipment", "EQUIPMENT", "text"],
    ["medium", "PHOTO, VIDEO OR BOTH", "select", ["Photo", "Video", "Both"]],
    ["turnaround", "TURNAROUND TIME", "text"],
    ["rates", "RATES OR COLLABORATION EXPECTATIONS", "textarea"],
  ],
  Vendor: [
    ["product", "PRODUCT CATEGORY", "text"],
    ["store", "STORE OR PORTFOLIO LINK", "url"],
    ["table_power", "TABLE & POWER REQUIREMENTS", "textarea"],
    ["rates", "RATES OR PARTICIPATION EXPECTATIONS", "textarea"],
  ],
  "Volunteer or production support": [
    ["area", "AREA OF INTEREST", "text"],
    ["experience", "RELEVANT EXPERIENCE", "textarea"],
    ["availability", "AVAILABILITY", "text"],
    ["responsibilities", "PREFERRED RESPONSIBILITIES", "textarea"],
  ],
};

const DEFAULT_FIELDS = [
  ["discipline", "DISCIPLINE / MEDIUM", "text"],
  ["portfolio", "PORTFOLIO OR WORK LINK", "url"],
  ["experience", "RELEVANT EXPERIENCE", "textarea"],
  ["availability", "AVAILABILITY", "text"],
];

function Field({ def, value, onChange, testId }) {
  const [key, label, type, options] = def;
  if (type === "textarea") {
    return (
      <div className="ank-field">
        <label htmlFor={`f-${key}`}>{label}</label>
        <textarea id={`f-${key}`} data-testid={testId} rows={3} value={value} onChange={(e) => onChange(e.target.value)} />
      </div>
    );
  }
  if (type === "select") {
    return (
      <div className="ank-field">
        <label htmlFor={`f-${key}`}>{label}</label>
        <select id={`f-${key}`} data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">Select…</option>
          {options.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      </div>
    );
  }
  return (
    <div className="ank-field">
      <label htmlFor={`f-${key}`}>{label}</label>
      <input
        id={`f-${key}`}
        data-testid={testId}
        type={type === "url" ? "url" : "text"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={type === "url" ? "https://…" : ""}
      />
    </div>
  );
}

export default function OpenCallForm({ applicationsOpen }) {
  const [step, setStep] = useState(0);
  const [category, setCategory] = useState("");
  const [basics, setBasics] = useState({
    full_name: "", artist_name: "", email: "", phone: "",
    city_state: "", social_url: "", portfolio_url: "", bio: "",
  });
  const [details, setDetails] = useState({});
  const [common, setCommon] = useState({ why: "", discover: "", retain_ok: false, join_list: false });
  const [honeypot, setHoneypot] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    try { window.posthog?.capture("opencall_start"); } catch {}
  }, []);

  const fields = CATEGORY_FIELDS[category] || DEFAULT_FIELDS;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!common.why.trim() || !common.discover.trim()) {
      setError("Please answer the two final questions.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/applications", {
        category,
        ...basics,
        details,
        ...common,
        website: honeypot,
      });
      try { window.posthog?.capture("opencall_complete", { category }); } catch {}
      setSuccess(true);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (success) {
    return (
      <div className="ank-success" data-testid="opencall-success">
        <p className="ank-success-title">YOUR SIGNAL WAS RECEIVED.</p>
        <p className="ank-modal-copy">
          Thank you for sharing your work with ANKHARA. Every submission is reviewed, but submission
          does not guarantee placement. If your work fits an upcoming session, we'll contact you directly.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="opencall-form">
      {!applicationsOpen && (
        <p className="ank-form-note" data-testid="opencall-paused-note">
          Event-specific open calls are currently paused — the archive remains open, and your
          submission will be kept for future programming.
        </p>
      )}
      <div className="ank-steps" data-testid="opencall-steps">
        {[1, 2, 3].map((n, i) => (
          <span key={n} className={`ank-step ${step === i ? "on" : ""} ${step > i ? "done" : ""}`}>
            {String(n).padStart(2, "0")}
          </span>
        ))}
      </div>

      {step === 0 && (
        <div>
          <p className="ank-step-label">STEP 01 — WHAT DO YOU MAKE?</p>
          <div className="ank-cat-grid" data-testid="opencall-categories">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                type="button"
                className={`ank-chip ${category === c ? "on" : ""}`}
                data-testid={`opencall-cat-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                onClick={() => setCategory(c)}
              >
                {c.toUpperCase()}
              </button>
            ))}
          </div>
          <div className="ank-form-nav">
            <button
              type="button"
              className="ank-submit"
              disabled={!category}
              data-testid="opencall-next-1"
              onClick={() => setStep(1)}
            >
              CONTINUE →
            </button>
          </div>
        </div>
      )}

      {step === 1 && (
        <form onSubmit={(e) => { e.preventDefault(); setStep(2); }}>
          <p className="ank-step-label">STEP 02 — BASIC INFORMATION</p>
          <div className="ank-form">
            <div className="ank-field-row">
              <div className="ank-field">
                <label htmlFor="oc-name">FULL NAME *</label>
                <input id="oc-name" data-testid="oc-full-name" required value={basics.full_name}
                  onChange={(e) => setBasics({ ...basics, full_name: e.target.value })} />
              </div>
              <div className="ank-field">
                <label htmlFor="oc-artist">ARTIST / PROFESSIONAL NAME</label>
                <input id="oc-artist" data-testid="oc-artist-name" value={basics.artist_name}
                  onChange={(e) => setBasics({ ...basics, artist_name: e.target.value })} />
              </div>
            </div>
            <div className="ank-field-row">
              <div className="ank-field">
                <label htmlFor="oc-email">EMAIL *</label>
                <input id="oc-email" data-testid="oc-email" type="email" required value={basics.email}
                  onChange={(e) => setBasics({ ...basics, email: e.target.value })} />
              </div>
              <div className="ank-field">
                <label htmlFor="oc-phone">PHONE (OPTIONAL)</label>
                <input id="oc-phone" data-testid="oc-phone" value={basics.phone}
                  onChange={(e) => setBasics({ ...basics, phone: e.target.value })} />
              </div>
            </div>
            <div className="ank-field">
              <label htmlFor="oc-city">CITY & STATE *</label>
              <input id="oc-city" data-testid="oc-city" required value={basics.city_state}
                onChange={(e) => setBasics({ ...basics, city_state: e.target.value })} />
            </div>
            <div className="ank-field-row">
              <div className="ank-field">
                <label htmlFor="oc-social">INSTAGRAM / MAIN SOCIAL PROFILE</label>
                <input id="oc-social" data-testid="oc-social" type="url" placeholder="https://…"
                  value={basics.social_url} onChange={(e) => setBasics({ ...basics, social_url: e.target.value })} />
              </div>
              <div className="ank-field">
                <label htmlFor="oc-portfolio">WEBSITE / PORTFOLIO</label>
                <input id="oc-portfolio" data-testid="oc-portfolio" type="url" placeholder="https://…"
                  value={basics.portfolio_url} onChange={(e) => setBasics({ ...basics, portfolio_url: e.target.value })} />
              </div>
            </div>
            <div className="ank-field">
              <label htmlFor="oc-bio">BRIEF BIO</label>
              <textarea id="oc-bio" data-testid="oc-bio" rows={3} value={basics.bio}
                onChange={(e) => setBasics({ ...basics, bio: e.target.value })} />
            </div>
          </div>
          <div className="ank-form-nav">
            <button type="button" className="ank-ghost-btn" data-testid="opencall-back-1" onClick={() => setStep(0)}>← BACK</button>
            <button type="submit" className="ank-submit" data-testid="opencall-next-2">CONTINUE →</button>
          </div>
        </form>
      )}

      {step === 2 && (
        <form onSubmit={submit}>
          <p className="ank-step-label">STEP 03 — {category.toUpperCase()} DETAILS</p>
          <div className="ank-form">
            {fields.map((def) => (
              <Field
                key={def[0]}
                def={def}
                testId={`oc-detail-${def[0]}`}
                value={details[def[0]] || ""}
                onChange={(v) => setDetails({ ...details, [def[0]]: v })}
              />
            ))}
            <div className="ank-field">
              <label htmlFor="oc-why">WHY DO YOU WANT TO BE PART OF ANKHARA? *</label>
              <textarea id="oc-why" data-testid="oc-why" rows={3} required value={common.why}
                onChange={(e) => setCommon({ ...common, why: e.target.value })} />
            </div>
            <div className="ank-field">
              <label htmlFor="oc-discover">HOW DID YOU DISCOVER ANKHARA? *</label>
              <input id="oc-discover" data-testid="oc-discover" required value={common.discover}
                onChange={(e) => setCommon({ ...common, discover: e.target.value })} />
            </div>
            <label className="ank-check" data-testid="oc-retain">
              <input type="checkbox" checked={common.retain_ok}
                onChange={(e) => setCommon({ ...common, retain_ok: e.target.checked })} />
              <span>ANKHARA may retain this submission for future opportunities.</span>
            </label>
            <label className="ank-check" data-testid="oc-join-list">
              <input type="checkbox" checked={common.join_list}
                onChange={(e) => setCommon({ ...common, join_list: e.target.checked })} />
              <span>Separately, I'd also like to join the ANKHARA email list. (Optional — you're not subscribed unless you tick this.)</span>
            </label>
          </div>
          <input type="text" value={honeypot} onChange={(e) => setHoneypot(e.target.value)}
            className="ank-honeypot" tabIndex={-1} autoComplete="off" aria-hidden="true" />
          {error && <p className="ank-form-error" data-testid="opencall-error">{error}</p>}
          <div className="ank-form-nav">
            <button type="button" className="ank-ghost-btn" data-testid="opencall-back-2" onClick={() => setStep(1)}>← BACK</button>
            <button type="submit" className="ank-submit" disabled={busy} data-testid="opencall-submit-btn">
              {busy ? "TRANSMITTING…" : "SEND YOUR SIGNAL"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
